import { faEye } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from 'src/components/Loader';
import {
  type VisitInterval,
  fetchVisitIntervals,
} from 'src/models/user-package/utils';
import { Identifier } from 'src/types/utilityTypes';
import { formatFullDateAndTime, formatShortDate } from 'src/utils/dateFormatter';

import { Box } from '../components/Box';
import { Chip } from '../components/Chip';
import { Dialog } from '../components/Dialog';
import { Icon } from '../components/Icon';
import { IconButton } from '../components/IconButton';
import { Stack } from '../components/Stack';
import { Typography } from '../components/Typography';

interface VisitIntervalsButtonProps {
  userPackageId: Identifier;
  serviceCountIndex: number;
  variant?: 'icon' | 'text';
}

export const VisitIntervalsButton: React.FC<VisitIntervalsButtonProps> = ({
  userPackageId,
  serviceCountIndex,
  variant = 'icon',
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'packages.editPackageDialog',
  });
  const [open, setOpen] = useState(false);

  return (
    <>
      {variant === 'icon' ? (
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          sx={{ verticalAlign: 'middle', ml: '-5px' }}
        >
          <Icon icon={faEye} size="xsmall" />
        </IconButton>
      ) : (
        <Typography
          variant="body2"
          color="primary"
          onClick={() => setOpen(true)}
          sx={{ cursor: 'pointer' }}
          component="span"
        >
          {t('visitIntervals')}
        </Typography>
      )}
      {open && (
        <VisitIntervalsDialog
          open={open}
          onClose={() => setOpen(false)}
          userPackageId={userPackageId}
          serviceCountIndex={serviceCountIndex}
        />
      )}
    </>
  );
};

const VisitIntervalsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  userPackageId: Identifier;
  serviceCountIndex: number;
}> = ({ open, onClose, userPackageId, serviceCountIndex }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'packages.editPackageDialog',
  });

  const { data: intervals = [], isLoading } = useQuery({
    queryKey: ['visit-intervals', userPackageId],
    queryFn: () => fetchVisitIntervals(userPackageId),
    enabled: open,
  });

  const filtered = intervals.filter(
    (i: VisitInterval) => i.serviceCountIndex === serviceCountIndex
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('visitIntervals')}
      subtitle={t('visitIntervalsSubtitle')}
      maxWidth="xs"
    >
      {isLoading ? (
        <Loader size="medium" />
      ) : filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('noIntervalData')}
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="right"
            sx={{ px: 1.5 }}
          >
            {t('remaining/limit')}
          </Typography>
          {filtered.map((interval, idx) => (
            <Box
              key={idx}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: interval.isCurrent
                  ? 'action.selected'
                  : 'transparent',
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">
                  {formatShortDate(interval.startDate + 'T00:00:00')} –{' '}
                  {formatShortDate(interval.endDate + 'T00:00:00')}
                  {interval.isCurrent && (
                    <Chip
                      label={t('current')}
                      color="primary"
                      size="small"
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {interval.remaining} / {interval.maxVisits}
                </Typography>
              </Stack>
              {interval.visits.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: 1, pl: 1 }}>
                  {interval.visits.map((visit) => (
                    <Stack
                      key={visit.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ minWidth: 120 }}
                      >
                        {formatFullDateAndTime(visit.date)}
                      </Typography>
                      <Typography variant="caption">
                        {visit.serviceName}
                      </Typography>
                      <Chip
                        label={visit.status}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Dialog>
  );
};
